import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Copy, Check, Share2, AlertCircle } from 'react-feather';
import { generateReferenceHash } from '../../../utils/hash';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: ${p => p.theme.isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(15, 23, 42, 0.4)'};
  z-index: 2500;
  display: ${({ show }) => (show ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
  padding: 12px;
  animation: ${fadeIn} 0.18s ease-out;
`;

const ModalContent = styled.div`
  background-color: ${p => p.theme.colors.background.paper};
  width: 100%;
  max-width: 460px;
  border-radius: 20px;
  box-shadow: ${p => p.theme.isDark 
    ? '0 20px 50px -12px rgba(0, 0, 0, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.05)' 
    : '0 20px 50px -12px rgba(15, 23, 42, 0.12)'};
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  border: none;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 10px;
`;

const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 800;
  margin: 0;
  color: ${p => p.theme.colors.text.primary};
  letter-spacing: -0.3px;
`;

const CloseButton = styled.button`
  background: ${p => p.theme.isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'};
  border: none;
  color: ${p => p.theme.colors.text.secondary};
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    background: ${p => p.theme.colors.background.selected};
    color: ${p => p.theme.colors.text.primary};
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 0 20px 20px;
`;

const InstructionText = styled.p`
  font-size: 12.5px;
  color: ${p => p.theme.colors.text.secondary};
  margin-top: 0;
  margin-bottom: 14px;
  line-height: 1.45;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 14px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${p => p.theme.isDark ? 'rgba(255, 255, 255, 0.015)' : 'rgba(0, 0, 0, 0.01)'};
  border: 1px solid ${p => p.theme.isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)'};
  padding: 8px 12px;
  border-radius: 10px;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${p => p.theme.colors.background.selected};
    border-color: ${p => p.theme.colors.border.hover};
  }
`;

const InfoLabel = styled.span`
  font-weight: 700;
  color: ${p => p.theme.colors.text.primary};
  font-size: 12px;
  letter-spacing: -0.1px;
`;

const ValueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 70%;
`;

const InfoValue = styled.span`
  font-family: 'SFMono-Regular', Consolas, monospace;
  color: ${p => p.theme.colors.text.secondary};
  text-align: right;
  word-break: break-all;
  font-size: 11.5px;
  font-weight: 500;
`;

const MiniCopyButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.$copied ? '#10b981' : p.theme.colors.text.disabled};
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
  
  &:hover {
    color: ${p => p.$copied ? '#10b981' : p.theme.colors.text.primary};
  }
`;

const WarningBox = styled.div`
  background: ${p => p.theme.isDark ? 'rgba(254, 240, 138, 0.12)' : '#fef9c3'};
  border-left: 4px solid ${p => p.theme.isDark ? '#facc15' : '#eab308'};
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const WarningText = styled.span`
  font-size: 13px;
  color: ${p => p.theme.isDark ? '#fef08a' : '#713f12'};
  font-weight: 700;
  line-height: 1.45;
`;

const ActionFooter = styled.div`
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  gap: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px;
  border-radius: 12px;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  
  ${p => p.$primary ? `
    background: ${p.theme.colors.text.primary};
    color: ${p.theme.colors.text.contrast};
    border: none;
    &:hover {
      opacity: 0.9;
    }
  ` : `
    background: ${p.theme.colors.background.selected};
    border: 1px solid ${p.theme.colors.border.main};
    color: ${p.theme.colors.text.primary};
    &:hover {
      background: ${p.theme.colors.border.hover};
    }
  `}
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: ${p => p.theme.isDark ? '#1f2937' : '#111827'};
  color: #ffffff;
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  z-index: 3000;
  display: flex;
  align-items: center;
  gap: 6px;
  animation: ${fadeIn} 0.2s ease-out;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

export default function FedwireInstructionsModal({ show, onClose, account }) {
  const [copied, setCopied] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  if (!account) return null;

  const refHash = generateReferenceHash(account.accountNumber || account.account || '1234567890');

  const wireDetails = {
    bankName: 'Cross River Bank',
    bankAddress: '885 Teaneck Road, Teaneck, NJ 07666, USA',
    routing: '021214891',
    swift: 'CRVBUS33',
    accountNumber: account.accountNumber || account.account || '1234567890',
    beneficiaryName: 'NYBanq',
    beneficiaryAddress: '123 Main Street, New York, NY 10001, USA',
    reference: refHash
  };

  const labels = {
    bankName: 'Bank Name',
    routing: 'ABA / Routing',
    swift: 'SWIFT / BIC',
    accountNumber: 'Account Number',
    beneficiaryName: 'Beneficiary Name',
    beneficiaryAddress: 'Beneficiary Addr',
    bankAddress: 'Bank Address',
    reference: 'Reference Hash'
  };

  const textBlock = `Receiving Bank Name: ${wireDetails.bankName}
ABA / Routing Number: ${wireDetails.routing}
SWIFT / BIC Code: ${wireDetails.swift}
Beneficiary Account Number: ${wireDetails.accountNumber}
Beneficiary Account Name: ${wireDetails.beneficiaryName}
Beneficiary Address: ${wireDetails.beneficiaryAddress}
Bank Address: ${wireDetails.bankAddress}
Reference: ${wireDetails.reference}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(textBlock);
    setCopied(true);
    setToastMessage('Copied all instructions!');
    setShowToast(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
    setTimeout(() => {
      setShowToast(false);
    }, 2500);
  };

  const handleCopyField = (key, val) => {
    navigator.clipboard.writeText(val);
    setCopiedField(key);
    setToastMessage(`Copied ${labels[key]}!`);
    setShowToast(true);
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);
    setTimeout(() => {
      setShowToast(false);
    }, 2500);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'NYBANQ International Wire Instructions',
        text: textBlock
      }).catch(err => {
        // Fallback
      });
    } else {
      handleCopy();
    }
  };

  return (
    <>
      <ModalOverlay show={show} onClick={onClose}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>International Wire Details</ModalTitle>
            <CloseButton onClick={onClose} aria-label="Close modal">
              <X size={14} />
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            <InstructionText>
              Provide these details to the sender to receive an international wire transfer (SWIFT) directly into your NYBANQ USD account.
            </InstructionText>
            
            <InfoList>
              {Object.entries(wireDetails).map(([key, val]) => (
                <InfoRow key={key}>
                  <InfoLabel>{labels[key]}</InfoLabel>
                  <ValueWrapper>
                    <InfoValue>{val}</InfoValue>
                    <MiniCopyButton 
                      $copied={copiedField === key} 
                      onClick={() => handleCopyField(key, val)}
                      aria-label={`Copy ${labels[key]}`}
                    >
                      {copiedField === key ? <Check size={11} /> : <Copy size={11} />}
                    </MiniCopyButton>
                  </ValueWrapper>
                </InfoRow>
              ))}
            </InfoList>
            
            <WarningBox>
              <AlertCircle size={14} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '1px' }} />
              <WarningText>
                IMPORTANT: The sender must include the exact Reference Hash in the wire details to avoid failures.
              </WarningText>
            </WarningBox>
            
            <ActionFooter>
              <ActionButton onClick={handleCopy} $primary>
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied All!' : 'Copy Instructions'}
              </ActionButton>
              <ActionButton onClick={handleShare}>
                <Share2 size={14} /> Share
              </ActionButton>
            </ActionFooter>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
      
      {showToast && (
        <Toast>
          <Check size={14} color="#10b981" />
          {toastMessage}
        </Toast>
      )}
    </>
  );
}
